import {
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    StopOutlined,
    TeamOutlined,
    UserOutlined,
    DownOutlined,
    UpOutlined,
    KeyOutlined,
    CrownOutlined,
    ToolOutlined,
    CopyOutlined
} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    Progress,
    Collapse,
    Tooltip,
    Badge,
    Divider
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { CreateOrganizationRequest, Organization, OrganizationMember, OrganizationService } from '../services/organization';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const OrganizationManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [quotaModalVisible, setQuotaModalVisible] = useState(false);
  const [limitsModalVisible, setLimitsModalVisible] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set());
  const [orgMembers, setOrgMembers] = useState<Record<number, OrganizationMember[]>>({});
  const [membersLoading, setMembersLoading] = useState<Set<number>>(new Set());
  const [form] = Form.useForm();
  const [quotaForm] = Form.useForm();
  const [limitsForm] = Form.useForm();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await OrganizationService.getAllOrganizations();
      setOrganizations(response.organizations);
    } catch (error: any) {
      message.error('获取组织列表失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (values: CreateOrganizationRequest) => {
    try {
      await OrganizationService.createOrganization({
        ...values,
        expiresAt: values.expiresAt ? dayjs(values.expiresAt).toISOString() : undefined,
      });
      message.success('组织创建成功');
      setModalVisible(false);
      form.resetFields();
      fetchOrganizations();
    } catch (error: any) {
      message.error('创建组织失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateQuota = async (values: { amount: number; description?: string }) => {
    if (!selectedOrganization) return;
    
    try {
      await OrganizationService.addOrganizationQuota(selectedOrganization.id, values.amount, values.description);
      message.success('额度添加成功');
      setQuotaModalVisible(false);
      quotaForm.resetFields();
      fetchOrganizations();
    } catch (error: any) {
      message.error('添加额度失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateLimits = async (values: { maxAdmins: number; maxWorkers: number }) => {
    if (!selectedOrganization) return;
    
    try {
      await OrganizationService.updateOrganizationLimits(selectedOrganization.id, values.maxAdmins, values.maxWorkers);
      message.success('用户限制更新成功');
      setLimitsModalVisible(false);
      limitsForm.resetFields();
      fetchOrganizations();
    } catch (error: any) {
      message.error('更新用户限制失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteOrganization = async (id: number) => {
    try {
      await OrganizationService.deleteOrganization(id);
      message.success('组织删除成功');
      fetchOrganizations();
    } catch (error: any) {
      message.error('删除组织失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleOrgExpanded = async (orgId: number) => {
    const newExpanded = new Set(expandedOrgs);
    
    if (expandedOrgs.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
      // 加载成员列表
      if (!orgMembers[orgId]) {
        setMembersLoading(prev => new Set(prev).add(orgId));
        try {
          const response = await OrganizationService.getOrganizationUsers(orgId);
          setOrgMembers(prev => ({ ...prev, [orgId]: response.users }));
        } catch (error: any) {
          message.error('获取成员列表失败: ' + (error.response?.data?.error || error.message));
        } finally {
          setMembersLoading(prev => {
            const newSet = new Set(prev);
            newSet.delete(orgId);
            return newSet;
          });
        }
      }
    }
    
    setExpandedOrgs(newExpanded);
  };


  const showQuotaModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    quotaForm.setFieldsValue({ amount: 100, description: '' }); // 默认增加100次额度
    setQuotaModalVisible(true);
  };

  const showLimitsModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    limitsForm.setFieldsValue({ 
      maxAdmins: organization.maxAdminCount === -1 ? 0 : organization.maxAdminCount,
      maxWorkers: organization.maxWorkerCount === -1 ? 0 : organization.maxWorkerCount
    });
    setLimitsModalVisible(true);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    });
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      active: { color: 'green', icon: <CheckCircleOutlined />, text: '活跃' },
      suspended: { color: 'orange', icon: <ClockCircleOutlined />, text: '暂停' },
      expired: { color: 'red', icon: <StopOutlined />, text: '过期' },
    };
    const config = statusMap[status as keyof typeof statusMap] || statusMap.active;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getQuotaDisplay = (quota: number) => {
    if (quota === -1) {
      return <Tag color="blue">无限制</Tag>;
    }
    return (
      <Tag color={quota > 100 ? 'green' : quota > 10 ? 'orange' : 'red'}>
        {quota} 次
      </Tag>
    );
  };


  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          组织管理
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          创建组织
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总组织数"
              value={organizations.length}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="活跃组织"
              value={organizations.filter(org => org.status === 'active').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="总用户数"
              value={organizations.reduce((sum, org) => sum + org.adminCount + org.workerCount, 0)}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 组织卡片列表 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
          <Card loading />
        ) : (
          organizations.map((org) => (
            <Card key={org.id} style={{ marginBottom: '16px' }}>
              <Row gutter={24}>
                {/* 左侧：基本信息 */}
                <Col span={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Title level={4} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TeamOutlined />
                      {org.name}
                      {getStatusTag(org.status)}
                      <Tooltip title="修改状态功能开发中">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          disabled
                          style={{ marginLeft: 'auto' }}
                        />
                      </Tooltip>
                    </Title>
                    <Text type="secondary" style={{ marginBottom: '12px', display: 'block' }}>
                      {org.description || '无描述'}
                    </Text>
                    <Text type="secondary">
                      <CalendarOutlined /> 创建时间：{dayjs(org.createdAt).format('YYYY-MM-DD HH:mm')}
                    </Text>
                  </div>

                  {/* 当前额度 */}
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>当前额度：</Text>
                    {getQuotaDisplay(org.currentQuota)}
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<EditOutlined />}
                      onClick={() => showQuotaModal(org)}
                    >
                      增加额度
                    </Button>
                  </div>

                  {/* 操作按钮 */}
                  <Space>
                    <Popconfirm
                      title="确定删除此组织？"
                      description="删除组织将同时删除组织内所有用户和数据，此操作不可撤销！"
                      onConfirm={() => handleDeleteOrganization(org.id)}
                      okText="确定"
                      cancelText="取消"
                      okType="danger"
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                </Col>

                {/* 右侧：邀请码管理和用户统计 */}
                <Col span={12}>
                  {/* 邀请码管理 */}
                  <div style={{ marginBottom: '20px' }}>
                    <Text strong style={{ marginBottom: '12px', display: 'block' }}>
                      <KeyOutlined /> 邀请码管理
                    </Text>
                    
                    {/* 管理员邀请码 */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Text>
                          <CrownOutlined /> 管理员邀请码
                        </Text>
                        <Text type="secondary">
                          {org.adminCount} / {org.maxAdminCount === -1 ? '∞' : org.maxAdminCount}
                        </Text>
                      </div>
                      
                      {org.adminInviteCode && (
                        <div style={{ marginBottom: '8px' }}>
                          <Input.Group compact>
                            <Input
                              value={org.adminInviteCode}
                              readOnly
                              style={{ width: 'calc(100% - 80px)' }}
                              size="small"
                            />
                            <Button
                              size="small"
                              onClick={() => copyToClipboard(org.adminInviteCode!, '管理员邀请码')}
                              style={{ width: '80px' }}
                            >
                              复制
                            </Button>
                          </Input.Group>
                        </div>
                      )}
                      
                      <Progress
                        percent={org.maxAdminCount === -1 ? 0 : Math.round((org.adminCount / org.maxAdminCount) * 100)}
                        strokeColor={org.maxAdminCount === -1 ? '#52c41a' : undefined}
                        showInfo={false}
                        size="small"
                      />
                    </div>

                    {/* 工作者邀请码 */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Text>
                          <ToolOutlined /> 工作者邀请码
                        </Text>
                        <Text type="secondary">
                          {org.workerCount} / {org.maxWorkerCount === -1 ? '∞' : org.maxWorkerCount}
                        </Text>
                      </div>
                      
                      {org.workerInviteCode && (
                        <div style={{ marginBottom: '8px' }}>
                          <Input.Group compact>
                            <Input
                              value={org.workerInviteCode}
                              readOnly
                              style={{ width: 'calc(100% - 80px)' }}
                              size="small"
                            />
                            <Button
                              size="small"
                              onClick={() => copyToClipboard(org.workerInviteCode!, '工作者邀请码')}
                              style={{ width: '80px' }}
                            >
                              复制
                            </Button>
                          </Input.Group>
                        </div>
                      )}
                      
                      <Progress
                        percent={org.maxWorkerCount === -1 ? 0 : Math.round((org.workerCount / org.maxWorkerCount) * 100)}
                        strokeColor={org.maxWorkerCount === -1 ? '#52c41a' : undefined}
                        showInfo={false}
                        size="small"
                      />
                    </div>
                    
                    {/* 编辑限制按钮 */}
                    <div style={{ textAlign: 'right', marginTop: '12px' }}>
                      <Button 
                        type="link" 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => showLimitsModal(org)}
                      >
                        编辑用户限制
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* 用户列表 - 可折叠 */}
              <Divider />
              <div>
                <Button
                  type="text"
                  icon={expandedOrgs.has(org.id) ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => toggleOrgExpanded(org.id)}
                  style={{ marginBottom: '16px' }}
                >
                  用户详情 ({org.adminCount + org.workerCount} 位用户)
                </Button>
                
                {expandedOrgs.has(org.id) && (
                  <Table
                    columns={[
                      {
                        title: '用户ID',
                        dataIndex: 'id',
                        key: 'id',
                        width: 80,
                      },
                      {
                        title: '用户名',
                        dataIndex: 'username',
                        key: 'username',
                      },
                      {
                        title: '用户类型',
                        dataIndex: 'role',
                        key: 'role',
                        render: (role) => (
                          <Tag color={role === 'admin' ? 'gold' : 'blue'}>
                            {role === 'admin' ? '管理员' : '工作者'}
                          </Tag>
                        ),
                      },
                      {
                        title: '加入时间',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
                      },
                      {
                        title: '操作',
                        key: 'actions',
                        render: (_, member) => (
                          <Space size="small">
                            <Tooltip title="用户编辑功能开发中">
                              <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                disabled
                              >
                                编辑
                              </Button>
                            </Tooltip>
                            <Tooltip title="用户删除功能开发中">
                              <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled
                              >
                                删除
                              </Button>
                            </Tooltip>
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={orgMembers[org.id] || []}
                    rowKey="id"
                    loading={membersLoading.has(org.id)}
                    pagination={{
                      pageSize: 5,
                      size: 'small',
                      showTotal: (total) => `共 ${total} 位成员`,
                    }}
                    size="small"
                  />
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* 创建组织模态框 */}
      <Modal
        title="创建新组织"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleCreateOrganization}
          layout="vertical"
        >
          <Form.Item
            label="组织名称"
            name="name"
            rules={[{ required: true, message: '请输入组织名称' }]}
          >
            <Input placeholder="请输入组织名称" />
          </Form.Item>
          
          <Form.Item
            label="组织描述"
            name="description"
          >
            <TextArea rows={3} placeholder="请输入组织描述（可选）" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="最大管理员数量"
                name="maxAdmins"
                initialValue={5}
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="最大管理员数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="最大工作者数量"
                name="maxWorkers"
                initialValue={20}
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="最大工作者数量" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="初始额度"
                name="initialQuota"
                initialValue={0}
              >
                <InputNumber min={-1} style={{ width: '100%' }} placeholder="初始额度（-1表示无限制）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="过期时间"
                name="expiresAt"
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  placeholder="选择过期时间（可选）"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建组织
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


      {/* 增加额度模态框 */}
      <Modal
        title="增加额度"
        open={quotaModalVisible}
        onCancel={() => {
          setQuotaModalVisible(false);
          quotaForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedOrganization && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text>组织: <Text strong>{selectedOrganization.name}</Text></Text>
              <br />
              <Text>当前额度: {getQuotaDisplay(selectedOrganization.currentQuota)}</Text>
            </div>
            
            <Form
              form={quotaForm}
              onFinish={handleUpdateQuota}
              layout="vertical"
            >
              <Form.Item
                label="增加数量"
                name="amount"
                rules={[
                  { required: true, message: '请输入增加的额度数量' },
                  { type: 'number', min: 1, message: '增加数量必须大于0' }
                ]}
                extra="请输入要为该组织增加的额度数量"
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="请输入增加的额度数量"
                />
              </Form.Item>

              <Form.Item
                label="备注说明"
                name="description"
                extra="记录本次增加额度的原因（可选）"
              >
                <TextArea
                  rows={3}
                  placeholder="请输入备注说明（可选）"
                />
              </Form.Item>
              
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setQuotaModalVisible(false);
                    quotaForm.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    增加额度
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 用户限制编辑模态框 */}
      <Modal
        title="编辑用户限制"
        open={limitsModalVisible}
        onCancel={() => {
          setLimitsModalVisible(false);
          limitsForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedOrganization && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text>组织: <Text strong>{selectedOrganization.name}</Text></Text>
              <br />
              <Text>当前管理员: <Text strong>{selectedOrganization.adminCount}</Text></Text>
              <br />
              <Text>当前工作者: <Text strong>{selectedOrganization.workerCount}</Text></Text>
            </div>
            
            <Form
              form={limitsForm}
              onFinish={handleUpdateLimits}
              layout="vertical"
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="最大管理员数"
                    name="maxAdmins"
                    rules={[{ required: true, message: '请输入最大管理员数' }]}
                    extra="输入 -1 表示无限制"
                  >
                    <InputNumber
                      min={-1}
                      style={{ width: '100%' }}
                      placeholder="最大管理员数"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="最大工作者数"
                    name="maxWorkers"
                    rules={[{ required: true, message: '请输入最大工作者数' }]}
                    extra="输入 -1 表示无限制"
                  >
                    <InputNumber
                      min={-1}
                      style={{ width: '100%' }}
                      placeholder="最大工作者数"
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setLimitsModalVisible(false);
                    limitsForm.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    更新限制
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrganizationManagement;
